/**
 * The one part of Analytics somebody has to read.
 *
 * It answers "is anything wrong, and where" before the screen shows a single
 * chart. That ordering is the redesign: the previous version led with seven raw
 * counts and two line charts, which is a competent presentation of numbers and
 * a poor answer to the only urgent question this data can settle.
 *
 * Two things it does that counts alone cannot.
 *
 * It divides. "Bounced 102" is unreadable without a denominator — 102 of four
 * million is spotless, 102 of four hundred gets the account suspended — and the
 * denominator is the difference between a number and a fact.
 *
 * And it names the campaign. An organization-wide rate averages a bad send away:
 * ninety days of healthy traffic around one purchased list reads as a 2.4%
 * bounce rate while the campaign responsible is at 22.9%. The aggregate is
 * technically correct and operationally useless, so the band ends by pointing at
 * the row.
 */

import { Link } from "react-router-dom";

import { Icon } from "../components/Sprites";
import {
  findingsFor,
  formatRate,
  healthOf,
  ratesOf,
  THRESHOLDS,
  type Health,
  type Totals,
} from "./rates";
import type { AnalyticsGroup } from "../api/types";

interface HealthBandProps {
  totals: Totals;
  /** Campaign rows for the same period, used to name the worst offender. */
  groups?: AnalyticsGroup[];
  /** Sent below this, rates are noise and no finding is raised. */
  volumeFloor?: number;
}

export function HealthBand({ totals, groups, volumeFloor = 50 }: HealthBandProps) {
  const findings = findingsFor(totals, volumeFloor);
  const worst = worstCampaign(groups ?? [], volumeFloor);

  if (findings.length === 0) {
    return (
      <div className="oc-panel health-band health-band--ok">
        <div className="oc-panel__body health-band__body">
          <span className="health-band__glyph" aria-hidden="true">
            <Icon name="check" size={16} />
          </span>
          <div className="stack health-band__text">
            <span className="oc-headline-md">Delivery looks healthy</span>
            <p className="oc-body-sm oc-text-secondary" style={{ margin: 0 }}>
              {totals.sent < volumeFloor ? (
                <>
                  Only {totals.sent.toLocaleString()} message
                  {totals.sent === 1 ? "" : "s"} in this period — too few for a rate to
                  mean anything, so nothing is being judged. Rates are reported from{" "}
                  {volumeFloor} sends.
                </>
              ) : (
                <>
                  Bounce rate {formatRate(ratesOf(totals).bounced)} and complaint rate{" "}
                  {formatRate(ratesOf(totals).complained)}, both under the levels that
                  pause sending ({formatRate(THRESHOLDS.bounce.critical!)} and{" "}
                  {formatRate(THRESHOLDS.complaint.critical!)}).
                </>
              )}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const severity = findings[0]!.health;

  return (
    <div
      className={`oc-panel health-band health-band--${severity}`}
      role={severity === "critical" ? "alert" : undefined}
    >
      <div className="oc-panel__body stack">
        <div className="health-band__body">
          <span className="health-band__glyph" aria-hidden="true">
            <Icon name="warning" size={16} />
          </span>
          <div className="stack health-band__text">
            <span className="oc-headline-md">
              {severity === "critical"
                ? "Sending is at risk"
                : "Delivery needs attention"}
            </span>
            <ul className="health-band__findings">
              {findings.map((finding) => (
                <li key={finding.label}>
                  <span className={`health-band__rate health-band__rate--${finding.health}`}>
                    {finding.label}
                  </span>
                  <span className="oc-body-sm oc-text-secondary"> — {finding.detail}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {worst && (
          <p className="oc-body-sm health-band__culprit" style={{ margin: 0 }}>
            Most of it is{" "}
            {worst.key ? (
              <Link to={`/campaigns/${worst.key}`}>{worst.name ?? "one campaign"}</Link>
            ) : (
              <strong>transactional mail</strong>
            )}
            : {formatRate(worst.bounceRate)} of its {worst.sent.toLocaleString()} messages
            bounced, against {formatRate(ratesOf(totals).bounced)} across everything sent.
            An organization-wide rate averages a single bad list away, which is why this
            names the send rather than the total.
          </p>
        )}
      </div>
    </div>
  );
}

interface Culprit {
  key: string | null;
  name: string | null;
  sent: number;
  bounceRate: number;
}

/**
 * The campaign dragging the aggregate down, if one clearly is.
 *
 * Returns nothing unless the worst row is both above the warning threshold and
 * meaningfully worse than the organization as a whole. A campaign at 2.1% when
 * everything is at 2.0% is not a culprit, and pointing at one would send
 * somebody to investigate a send that is behaving normally.
 */
function worstCampaign(groups: AnalyticsGroup[], volumeFloor: number): Culprit | null {
  let worst: Culprit | null = null;

  for (const group of groups) {
    const { sent, bounced } = group.totals;
    if (sent < volumeFloor) continue;

    const bounceRate = bounced / sent;
    if (healthOf(bounceRate, THRESHOLDS.bounce) === "ok") continue;
    if (worst === null || bounceRate > worst.bounceRate) {
      worst = {
        key: group.key ?? null,
        name: group.name ?? null,
        sent,
        bounceRate,
      };
    }
  }

  return worst;
}

/** The colour a rate should be rendered in. Exported so the table agrees. */
export function healthClass(health: Health): string {
  return health === "ok" ? "" : ` rate--${health}`;
}
